import type { Theme } from '@ksyos/design-system';
import { DateTime } from '@ksyos/date-time';
import {
    Alert,
    Box,
    Button,
    ButtonRow,
    Col,
    Form,
    FormGroup,
    FormParagraph,
    FormSection,
    Heading,
    Modal,
    Radio,
    RadioGroup,
    Row,
    Select,
    Text,
    TextInput,
} from '@ksyos/design-system';
import { useId } from 'react';
import type { IInsuranceHistory, IOption } from '@frontend/components/interfaces';
import type { ValidationErrors } from '@frontend/utils';
import { Gender, MaritalStatus, State } from '@common/enums';
import { patientAddEditSchema } from '@common/validation/patient';
import { IndependentDateInput } from '@frontend/components/fields';
import { getSelectedLanguage } from '@frontend/translate';
import { enumToDropdownIOptions, getValidationErrorTranslation, validate } from '@frontend/utils';
import type { IPatient } from './PatientsAddEdit';

interface IPatientEditForm {
    activeInsurance: IInsuranceHistory | undefined;
    countryOptions: IOption<string>[];
    getCountryOption: (country: string | null) =>
        | {
              label: string | undefined;
              value: string;
          }
        | undefined;
    getMaritalStatusOption: (maritalStatus: MaritalStatus | null) =>
        | {
              label: string;
              value: string;
          }
        | undefined;
    getPractitionerAndOrganizationOption: (
        organizationId: string,
        practitionerId: string
    ) => IOption<string> | undefined;
    getStateOption: (state: string | null) =>
        | {
              label: State | undefined;
              value: string;
          }
        | undefined;
    gpOptions: IOption<string>[];
    handleSubmit: () => Promise<void>;
    isSaving: boolean;
    loadedPatient: IPatient;
    onClose: () => Promise<void> | void;
    patient: IPatient | null;
    patientConsent: boolean;
    setIsPatientDeathModalOpen: (isPatientDeathModalOpen: boolean) => void;
    setPatient: React.Dispatch<React.SetStateAction<IPatient | null>>;
    setPatientConsent: (patientConsent: boolean) => void;
    setValidationErrors: (errors: ValidationErrors) => void;
    updateAndValidateBirthDate: (loadedPatient: IPatient, date: string | null) => void;
    validateOne: (name: keyof IPatient) => void;
    validationErrors: ValidationErrors;
}

export function PatientEditForm(props: IPatientEditForm) {
    const {
        activeInsurance,
        countryOptions,
        getCountryOption,
        getMaritalStatusOption,
        getPractitionerAndOrganizationOption,
        getStateOption,
        gpOptions,
        handleSubmit,
        isSaving,
        loadedPatient,
        onClose,
        patient,
        patientConsent,
        setIsPatientDeathModalOpen,
        setPatient,
        setPatientConsent,
        setValidationErrors,
        updateAndValidateBirthDate,
        validateOne,
        validationErrors,
    } = props;

    const houseNumberId = useId();
    const cityId = useId();
    const telephoneId = useId();

    return (
        <Form
            onSubmit={async (e) => {
                e.preventDefault();
                await handleSubmit();
            }}
            submitOnEnter={false}
        >
            <Modal.Body>
                <ButtonRow justify="right">
                    <Button
                        onClick={() => {
                            const { errors } = validate(patientAddEditSchema, patient);
                            setValidationErrors(errors);
                            if (!errors.size) {
                                setIsPatientDeathModalOpen(true);
                            }
                        }}
                        variant="link"
                    >
                        {getSelectedLanguage().patient.registerDeath}
                    </Button>
                </ButtonRow>

                <FormSection title={getSelectedLanguage().patient.details}>
                    <FormGroup
                        hasErrors={validationErrors.has('nameGiven')}
                        isRequired={true}
                        label={getSelectedLanguage().common.nameGiven}
                    >
                        <TextInput
                            error={getValidationErrorTranslation(validationErrors.get('nameGiven'))}
                            onBlur={() => validateOne('nameGiven')}
                            onChange={(e) => {
                                setPatient({
                                    ...loadedPatient,
                                    nameGiven: e.target.value,
                                });
                            }}
                            onEnterKeyPress={() => validateOne('nameGiven')}
                            placeholder="Voer de voornaam in"
                            value={loadedPatient.nameGiven}
                        />
                    </FormGroup>
                    <FormGroup
                        hasErrors={validationErrors.has('nameInfix')}
                        label={getSelectedLanguage().common.nameInfix}
                    >
                        <TextInput
                            error={getValidationErrorTranslation(validationErrors.get('nameInfix'))}
                            onBlur={() => validateOne('nameInfix')}
                            onChange={(e) =>
                                setPatient({
                                    ...loadedPatient,
                                    nameInfix: e.target.value.length > 0 ? e.target.value : null,
                                })
                            }
                            onEnterKeyPress={() => validateOne('nameInfix')}
                            placeholder={getSelectedLanguage().common.nameInfix}
                            value={loadedPatient.nameInfix ?? ''}
                        />
                    </FormGroup>
                    <FormGroup
                        hasErrors={validationErrors.has('nameFamily')}
                        isRequired={true}
                        label={getSelectedLanguage().common.nameFamily}
                    >
                        <TextInput
                            error={getValidationErrorTranslation(validationErrors.get('nameFamily'))}
                            onBlur={() => validateOne('nameFamily')}
                            onChange={(e) =>
                                setPatient({
                                    ...loadedPatient,
                                    nameFamily: e.target.value,
                                })
                            }
                            onEnterKeyPress={() => validateOne('nameFamily')}
                            placeholder={getSelectedLanguage().common.nameFamily}
                            value={loadedPatient.nameFamily}
                        />
                    </FormGroup>
                    <FormGroup
                        hasErrors={validationErrors.has('nameInfix2')}
                        label={getSelectedLanguage().patient.nameInfix2}
                    >
                        <TextInput
                            error={getValidationErrorTranslation(validationErrors.get('nameInfix2'))}
                            onBlur={() => validateOne('nameInfix2')}
                            onChange={(e) =>
                                setPatient({
                                    ...loadedPatient,
                                    nameInfix2: e.target.value.length > 0 ? e.target.value : null,
                                })
                            }
                            onEnterKeyPress={() => validateOne('nameInfix2')}
                            placeholder={getSelectedLanguage().patient.nameInfix2}
                            value={loadedPatient.nameInfix2 ?? ''}
                        />
                    </FormGroup>
                    <FormGroup
                        hasErrors={validationErrors.has('nameFamily2')}
                        label={getSelectedLanguage().patient.nameFamily2}
                    >
                        <TextInput
                            error={getValidationErrorTranslation(validationErrors.get('nameFamily2'))}
                            onBlur={() => validateOne('nameFamily2')}
                            onChange={(e) =>
                                setPatient({
                                    ...loadedPatient,
                                    nameFamily2: e.target.value.length > 0 ? e.target.value : null,
                                })
                            }
                            onEnterKeyPress={() => validateOne('nameFamily2')}
                            placeholder={getSelectedLanguage().patient.nameFamily2}
                            value={loadedPatient.nameFamily2 ?? ''}
                        />
                    </FormGroup>
                    <FormGroup
                        hasErrors={validationErrors.has('nameUsual')}
                        label={getSelectedLanguage().patient.nameUsual}
                    >
                        <TextInput
                            error={getValidationErrorTranslation(validationErrors.get('nameUsual'))}
                            onBlur={() => validateOne('nameUsual')}
                            onChange={(e) =>
                                setPatient({
                                    ...loadedPatient,
                                    nameUsual: e.target.value.length > 0 ? e.target.value : null,
                                })
                            }
                            onEnterKeyPress={() => validateOne('nameUsual')}
                            placeholder={getSelectedLanguage().patient.nameUsual}
                            value={loadedPatient.nameUsual ?? ''}
                        />
                    </FormGroup>
                    <FormGroup
                        hasErrors={validationErrors.has('gender')}
                        isRequired
                        label={getSelectedLanguage().common.gender}
                    >
                        <RadioGroup
                            error={getValidationErrorTranslation(validationErrors.get('gender'))}
                            layout="column"
                            name="gender"
                            onBlur={() => validateOne('gender')}
                            onChange={(e) =>
                                setPatient({
                                    ...loadedPatient,
                                    gender: e.target.value as Gender,
                                })
                            }
                            value={loadedPatient.gender!}
                        >
                            <Radio
                                label={getSelectedLanguage().genderEnum.female}
                                value={Gender.female}
                            />
                            <Radio
                                label={getSelectedLanguage().genderEnum.male}
                                value={Gender.male}
                            />
                            <Radio
                                label={getSelectedLanguage().genderEnum.other}
                                value={Gender.other}
                            />
                            <Radio
                                label={getSelectedLanguage().genderEnum.unknown}
                                value={Gender.unknown}
                            />
                        </RadioGroup>
                    </FormGroup>
                    <FormGroup
                        hasErrors={validationErrors.has('birthDate')}
                        isRequired
                        label={getSelectedLanguage().common.birthDate}
                    >
                        <IndependentDateInput
                            error={validationErrors.get('birthDate')}
                            hasMaxWidth={true}
                            id="birthDate"
                            maxDate={DateTime.now().format()}
                            onChange={(value) => updateAndValidateBirthDate(loadedPatient, value)}
                            onEnterPress={(value) => updateAndValidateBirthDate(loadedPatient, value)}
                            placeholder={getSelectedLanguage().common.datePlaceholder}
                            size="medium"
                            value={loadedPatient.birthDate}
                            variant="outline"
                        />
                    </FormGroup>
                    <FormGroup
                        hasErrors={validationErrors.has('identifierSocialSecurity')}
                        label={`${getSelectedLanguage().patient.identifierSocialSecurityLong} (${
                            getSelectedLanguage().patient.identifierSocialSecurity
                        })`}
                    >
                        <TextInput
                            error={getValidationErrorTranslation(validationErrors.get('identifierSocialSecurity'))}
                            onBlur={() => validateOne('identifierSocialSecurity')}
                            onChange={(e) =>
                                setPatient({
                                    ...loadedPatient,
                                    identifierSocialSecurity: e.target.value.length > 0 ? e.target.value : null,
                                })
                            }
                            onEnterKeyPress={() => validateOne('identifierSocialSecurity')}
                            placeholder={getSelectedLanguage().patient.identifierSocialSecurityLong}
                            value={loadedPatient.identifierSocialSecurity ?? ''}
                            variant="outline"
                        />
                    </FormGroup>
                    <FormGroup
                        hasErrors={validationErrors.has('maritalStatus')}
                        label={getSelectedLanguage().patient.maritalStatus}
                    >
                        <Select
                            error={getValidationErrorTranslation(validationErrors.get('maritalStatus'))}
                            isClearable
                            onBlur={() => validateOne('maritalStatus')}
                            onChange={(maritalStatus) => {
                                if (maritalStatus) {
                                    setPatient({
                                        ...loadedPatient,
                                        maritalStatus: maritalStatus.value as MaritalStatus,
                                    });
                                } else {
                                    setPatient({
                                        ...loadedPatient,
                                        maritalStatus: null,
                                    });
                                }
                            }}
                            options={enumToDropdownIOptions(MaritalStatus, 'maritalStatusEnum')}
                            placeholder={`${getSelectedLanguage().serviceGraderPool.chooseOption}...`}
                            value={getMaritalStatusOption(loadedPatient.maritalStatus)}
                        />
                    </FormGroup>
                    <FormGroup
                        hasErrors={validationErrors.has('profession')}
                        label={getSelectedLanguage().common.profession}
                    >
                        <TextInput
                            error={getValidationErrorTranslation(validationErrors.get('profession'))}
                            onBlur={() => validateOne('profession')}
                            onChange={(e) =>
                                setPatient({
                                    ...loadedPatient,
                                    profession: e.target.value.length > 0 ? e.target.value : null,
                                })
                            }
                            onEnterKeyPress={() => validateOne('profession')}
                            placeholder={getSelectedLanguage().common.profession}
                            value={loadedPatient.profession ?? ''}
                            variant="outline"
                        />
                    </FormGroup>
                </FormSection>
                <FormSection title={getSelectedLanguage().common.contactDetails}>
                    <FormGroup
                        hasErrors={validationErrors.has('street')}
                        label={getSelectedLanguage().common.street}
                    >
                        <TextInput
                            error={getValidationErrorTranslation(validationErrors.get('street'))}
                            onBlur={() => validateOne('street')}
                            onChange={(e) =>
                                setPatient({
                                    ...loadedPatient,
                                    street: e.target.value.length > 0 ? e.target.value : null,
                                })
                            }
                            onEnterKeyPress={() => validateOne('street')}
                            placeholder={getSelectedLanguage().common.street}
                            value={loadedPatient.street ?? ''}
                        />
                    </FormGroup>
                    <FormGroup
                        hasErrors={
                            validationErrors.has('houseNumber') ||
                            validationErrors.has('houseLetter') ||
                            validationErrors.has('houseNumberSuffix')
                        }
                        htmlFor={houseNumberId}
                        label={getSelectedLanguage().common.houseNumber}
                    >
                        <Row
                            columnGap={[2, 4]}
                            rowGap={[2, 4]}
                        >
                            <Col span={[12, null, 6]}>
                                <TextInput
                                    error={getValidationErrorTranslation(validationErrors.get('houseNumber'))}
                                    id={houseNumberId}
                                    onBlur={() => validateOne('houseNumber')}
                                    onChange={(e) =>
                                        setPatient({
                                            ...loadedPatient,
                                            houseNumber: e.target.value.length > 0 ? e.target.value : null,
                                        })
                                    }
                                    onEnterKeyPress={() => validateOne('houseNumber')}
                                    placeholder={getSelectedLanguage().common.houseNumber}
                                    value={loadedPatient.houseNumber ?? ''}
                                />
                            </Col>
                            <Col span={[6, null, 2]}>
                                <TextInput
                                    error={getValidationErrorTranslation(validationErrors.get('houseLetter'))}
                                    onBlur={() => validateOne('houseLetter')}
                                    onChange={(e) =>
                                        setPatient({
                                            ...loadedPatient,
                                            houseLetter: e.target.value.length > 0 ? e.target.value : null,
                                        })
                                    }
                                    onEnterKeyPress={() => validateOne('houseLetter')}
                                    placeholder={getSelectedLanguage().patient.houseLetterShort}
                                    value={loadedPatient.houseLetter ?? ''}
                                />
                            </Col>
                            <Col span={[6, null, 4]}>
                                <TextInput
                                    error={getValidationErrorTranslation(validationErrors.get('houseNumberSuffix'))}
                                    onBlur={() => validateOne('houseNumberSuffix')}
                                    onChange={(e) =>
                                        setPatient({
                                            ...loadedPatient,
                                            houseNumberSuffix: e.target.value.length > 0 ? e.target.value : null,
                                        })
                                    }
                                    onEnterKeyPress={() => validateOne('houseNumberSuffix')}
                                    placeholder={getSelectedLanguage().common.suffix}
                                    value={loadedPatient.houseNumberSuffix ?? ''}
                                />
                            </Col>
                        </Row>
                    </FormGroup>
                    <FormGroup
                        hasErrors={validationErrors.has('zip')}
                        label={getSelectedLanguage().common.zipcode}
                    >
                        <TextInput
                            error={getValidationErrorTranslation(validationErrors.get('zip'))}
                            onBlur={() => validateOne('zip')}
                            onChange={(e) =>
                                setPatient({
                                    ...loadedPatient,
                                    zip: e.target.value.length > 0 ? e.target.value : null,
                                })
                            }
                            onEnterKeyPress={() => validateOne('zip')}
                            placeholder={getSelectedLanguage().common.zipcode}
                            value={loadedPatient.zip ?? ''}
                        />
                    </FormGroup>
                    <FormGroup
                        hasErrors={validationErrors.has('city')}
                        htmlFor={cityId}
                        label={getSelectedLanguage().common.city}
                    >
                        <Row
                            columnGap={[2, 4]}
                            rowGap={[2, 4]}
                        >
                            <Col span={[12, null, 6]}>
                                <TextInput
                                    error={getValidationErrorTranslation(validationErrors.get('city'))}
                                    id={cityId}
                                    onBlur={() => validateOne('city')}
                                    onChange={(e) =>
                                        setPatient({
                                            ...loadedPatient,
                                            city: e.target.value.length > 0 ? e.target.value : null,
                                        })
                                    }
                                    onEnterKeyPress={() => validateOne('city')}
                                    placeholder={getSelectedLanguage().common.city}
                                    value={loadedPatient.city ?? ''}
                                />
                            </Col>
                            <Col span={[12, null, 6]}>
                                <Select
                                    error={getValidationErrorTranslation(validationErrors.get('state'))}
                                    isClearable
                                    onBlur={() => validateOne('state')}
                                    onChange={(state) => {
                                        if (state) {
                                            setPatient({
                                                ...loadedPatient,
                                                state: state.value as State,
                                            });
                                        } else {
                                            setPatient({
                                                ...loadedPatient,
                                                state: null,
                                            });
                                        }
                                    }}
                                    options={Object.entries(State).map((item) => {
                                        return {
                                            label: item[1],
                                            value: item[0],
                                        };
                                    })}
                                    placeholder={`${getSelectedLanguage().common.province}...`}
                                    value={getStateOption(loadedPatient.state)}
                                />
                            </Col>
                        </Row>
                    </FormGroup>
                    <FormGroup
                        hasErrors={validationErrors.has('country')}
                        label={getSelectedLanguage().common.country}
                    >
                        <Select
                            error={getValidationErrorTranslation(validationErrors.get('country'))}
                            isClearable
                            onBlur={() => validateOne('country')}
                            onChange={(country) => {
                                if (country) {
                                    setPatient({
                                        ...loadedPatient,
                                        country: country.value,
                                    });
                                } else {
                                    setPatient({
                                        ...loadedPatient,
                                        country: null,
                                    });
                                }
                            }}
                            options={countryOptions}
                            placeholder={getSelectedLanguage().common.countryPlaceholder}
                            value={getCountryOption(loadedPatient.country)}
                        />
                    </FormGroup>
                    <FormGroup
                        hasErrors={validationErrors.has('mobileNumber') || validationErrors.has('homePhoneNumber')}
                        htmlFor={telephoneId}
                        label={getSelectedLanguage().common.telephone}
                    >
                        <Row
                            columnGap={[2, 4]}
                            rowGap={[2, 4]}
                        >
                            <Col span={[12, null, 6]}>
                                <TextInput
                                    error={getValidationErrorTranslation(validationErrors.get('mobileNumber'))}
                                    id={telephoneId}
                                    onBlur={() => validateOne('mobileNumber')}
                                    onChange={(e) =>
                                        setPatient({
                                            ...loadedPatient,
                                            mobileNumber: e.target.value.length > 0 ? e.target.value : null,
                                        })
                                    }
                                    onEnterKeyPress={() => validateOne('mobileNumber')}
                                    placeholder={getSelectedLanguage().common.mobileNumber}
                                    value={loadedPatient.mobileNumber ?? ''}
                                />
                            </Col>
                            <Col span={[12, null, 6]}>
                                <TextInput
                                    error={getValidationErrorTranslation(validationErrors.get('homePhoneNumber'))}
                                    onBlur={() => validateOne('homePhoneNumber')}
                                    onChange={(e) =>
                                        setPatient({
                                            ...loadedPatient,
                                            homePhoneNumber: e.target.value.length > 0 ? e.target.value : null,
                                        })
                                    }
                                    onEnterKeyPress={() => validateOne('homePhoneNumber')}
                                    placeholder={getSelectedLanguage().common.homeNumber}
                                    value={loadedPatient.homePhoneNumber ?? ''}
                                />
                            </Col>
                        </Row>
                    </FormGroup>
                    <FormGroup
                        hasErrors={validationErrors.has('emailAddress')}
                        label={getSelectedLanguage().common.email}
                    >
                        <TextInput
                            error={getValidationErrorTranslation(validationErrors.get('emailAddress'))}
                            onBlur={() => validateOne('emailAddress')}
                            onChange={(e) =>
                                setPatient({
                                    ...loadedPatient,
                                    emailAddress: e.target.value.length > 0 ? e.target.value : null,
                                })
                            }
                            onEnterKeyPress={() => validateOne('emailAddress')}
                            placeholder={getSelectedLanguage().common.email}
                            value={loadedPatient.emailAddress ?? ''}
                        />
                    </FormGroup>
                </FormSection>
                <FormSection title={getSelectedLanguage().patient.careProviderPractitioner}>
                    <FormGroup
                        hasErrors={validationErrors.has('careProviderOrganizationId')}
                        isRequired
                        label={getSelectedLanguage().patient.careProvider}
                    >
                        <Select
                            error={getValidationErrorTranslation(validationErrors.get('careProviderOrganizationId'))}
                            noOptionsMessage={() => `${getSelectedLanguage().patient.selectGP}...`}
                            onChange={(practitionerAndOrganization) => {
                                if (!practitionerAndOrganization) {
                                    setValidationErrors(
                                        validationErrors.set('careProviderOrganizationId', 'any_required')
                                    );
                                } else {
                                    setValidationErrors(validationErrors.remove('careProviderOrganizationId'));
                                    setPatient({
                                        ...loadedPatient,
                                        careProviderOrganizationId:
                                            practitionerAndOrganization?.value.split(', ')[1] ?? null,
                                        careProviderPractitionerId:
                                            practitionerAndOrganization?.value.split(', ')[0] ?? null,
                                    });
                                }
                            }}
                            options={gpOptions}
                            placeholder={`${getSelectedLanguage().patient.selectGP}...`}
                            value={getPractitionerAndOrganizationOption(
                                loadedPatient.careProviderOrganizationId!,
                                loadedPatient.careProviderPractitionerId!
                            )}
                        />
                    </FormGroup>
                </FormSection>
                <FormSection>
                    <Box
                        alignItems="center"
                        display="flex"
                        justifyContent="space-between"
                        marginBottom={6}
                    >
                        <Heading size="small">{getSelectedLanguage().patient.identifierInsurance}</Heading>
                    </Box>
                    <FormGroup label={getSelectedLanguage().common.healthInsurer}>
                        <FormParagraph>{activeInsurance?.uzovi.name ?? '-'}</FormParagraph>
                    </FormGroup>
                    <FormGroup label={getSelectedLanguage().patient.policyNumber}>
                        <FormParagraph>{activeInsurance?.insurancePolicyNumber ?? '-'}</FormParagraph>
                    </FormGroup>
                </FormSection>
                <FormSection isBorderless>
                    <Alert appearance="info">
                        <Alert.Checkbox
                            checked={patientConsent}
                            label={
                                <Text
                                    fontWeight="bold"
                                    size="md"
                                    sx={{
                                        color: (theme: Theme) => theme.alert.info.foregroundColor,
                                    }}
                                >
                                    {getSelectedLanguage().patient.patientPermissionCheckbox}
                                </Text>
                            }
                            onChange={(e) => setPatientConsent(e.target.checked)}
                        />
                    </Alert>
                </FormSection>
            </Modal.Body>
            <Modal.Footer>
                <Button
                    isDisabled={isSaving}
                    onClick={() => onClose()}
                    variant="outline"
                >
                    {getSelectedLanguage().common.cancel}
                </Button>
                <Button
                    isDisabled={isSaving || !patientConsent}
                    type="submit"
                    variant="solid"
                >
                    {getSelectedLanguage().common.save}
                </Button>
            </Modal.Footer>
        </Form>
    );
}
